import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText,
  AppBar,
  Toolbar,
  useMediaQuery,
  useTheme,
  Fade,
  Slide,
  Chip,
  Avatar,
  Divider
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Close as CloseIcon,
  ArrowForward as ArrowIcon
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import logoImg from '../assests/logo.jpg';

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const navItems = [
    { name: 'Home', path: '/', color: '#F59E0B' },
    { name: 'Products', path: '/products', color: '#F59E0B' },
    { name: 'About', path: '/aboutus', color: '#F59E0B' }
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };



  return (
    <>
      {/* Main Navbar */}
        <AppBar 
        position="fixed" 
        elevation={0}
        sx={{
          backgroundColor: '#ffffff',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: theme.zIndex.appBar,
          boxShadow: scrolled ? '0 4px 20px rgba(0, 0, 0, 0.03)' : 'none',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ px: { xs: 0, md: 2 }, py: 1.5 }}>
            {/* Logo/Brand */}
            <Box sx={{ flexGrow: 0, mr: 4 }}>
              <Link to="/" style={{ textDecoration: 'none' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {/* Premium Logo Icon */}
                  <Box sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 4px 15px rgba(245,158,11, 0.3)',
                    }
                  }}>
                    <img src={logoImg} alt="PHOSAI Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                  
                  {/* Brand Name */}
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: '#111827',
                        fontSize: { xs: '1.4rem', md: '1.5rem' },
                        letterSpacing: '-0.04em',
                        lineHeight: 1,
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      PHOSAI
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        letterSpacing: '0.15em',
                        color: '#6B7280',
                        textTransform: 'uppercase',
                        lineHeight: 1,
                        mt: 0.25,
                      }}
                    >
                      AI for Africa
                    </Typography>
                  </Box>
                </Box>
              </Link>
            </Box>

            {/* Spacer to push navigation to the right */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}>
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Button
                      key={item.name}
                      component={Link}
                      to={item.path}
                      sx={{
                        color: isActive ? '#111827' : '#6B7280',
                        fontWeight: isActive ? 600 : 500,
                        fontSize: '0.9rem',
                        px: 2,
                        py: 1,
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        backgroundColor: 'transparent',
                        border: 'none',
                        '&:hover': {
                          color: '#111827',
                          backgroundColor: 'rgba(0, 0, 0, 0.03)',
                        },
                      }}
                    >
                      {item.name}
                    </Button>
                  );
                })}

              </Box>
            )}

            {/* Mobile Menu Button */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                display: { md: 'none' },
                color: '#111827',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                borderRadius: '12px',
                width: 44,
                height: 44,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 340,
            background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
            border: 'none',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        {/* Drawer Header */}
        <Box sx={{
          p: 4,
          borderBottom: '1px solid rgba(245,158,11, 0.2)',
          background: 'rgba(245,158,11, 0.05)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 900,
                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              PHOSAI
            </Typography>
            <IconButton
              onClick={handleDrawerToggle}
              sx={{ 
                color: 'white',
                backgroundColor: 'rgba(245,158,11, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(245,158,11, 0.3)',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          
          {/* Company Description */}
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#9CA3AF',
              lineHeight: 1.6,
              fontSize: '0.9rem',
            }}
          >
            World-class AI company specializing in machine translation for African languages.
          </Typography>
        </Box>

        {/* Drawer Navigation */}
        <List sx={{ p: 2 }}>
          {navItems.map((item) => (
            <ListItem 
              key={item.name}
              component={item.path.startsWith('#') ? 'button' : Link}
              to={item.path.startsWith('#') ? undefined : item.path}

              sx={{
                borderRadius: '18px',
                mb: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: `${item.color}15`,
                  transform: 'translateX(8px)',
                }
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                width: '100%',
                py: 1,
              }}>

                <ListItemText 
                  primary={item.name}
                  primaryTypographyProps={{
                    sx: {
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '1rem',
                    }
                  }}
                />
              </Box>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ borderColor: 'rgba(245,158,11, 0.2)', mx: 2, my: 2 }} />

        {/* Quick Actions */}
        <Box sx={{ p: 2 }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              color: '#9CA3AF',
              fontWeight: 600,
              mb: 2,
              px: 1,
            }}
          >
            Quick Actions
          </Typography>
          

        </Box>

        {/* Drawer Footer */}
        <Box sx={{ 
          p: 4, 
          mt: 'auto',
          borderTop: '1px solid rgba(245,158,11, 0.2)',
          background: 'rgba(245,158,11, 0.05)',
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#9CA3AF',
                fontSize: '0.8rem',
                mb: 2,
              }}
            >
              © 2024 PHOSAI. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
              <Chip
                label="AI-Powered"
                size="small"
                sx={{
                  backgroundColor: 'rgba(245,158,11, 0.2)',
                  color: '#F59E0B',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
              <Chip
                label="African Tech"
                size="small"
                sx={{
                  backgroundColor: 'rgba(245,158,11, 0.2)',
                  color: '#F59E0B',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Spacer for fixed navbar */}
      <Box sx={{ height: { xs: 80, md: 88 } }} />
    </>
  );
};

export default Navbar;
